import { Avatar, Badge, Button, Logo, Menu, Skeleton, ThemeToggle, cx, useToast, type MenuItem } from "@ml/ui";
import { useMutation } from "@tanstack/react-query";
import {
  Building2,
  GraduationCap,
  Gift,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  CircleUser,
  House,
  LogOut,
  Plus,
  Settings,
  ShieldCheck,
  UserRound,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { UNLIMITED_SEATS } from "@ml/shared";
import { useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, matchPath, useLocation, useMatch, useNavigate } from "react-router";
import { ExtensionGlyph, IgGlyph } from "../components/IgGlyph";
import { ADD_ACCOUNT_ANCHOR } from "../components/accounts/AddIgAccountGuide";
import { IgStatusAvatar, IgSwitcherOption } from "../components/accounts/IgSwitcherOption";
import { PlanGate } from "../components/plans/LockedFeature";
import { PlanLock } from "../components/plans/PlanCta";
import { featureNav } from "../features/registry";
import { authApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatCount, planLabel, ROLE_LABELS } from "../lib/format";
import { IgAccountProvider, useIgAccount } from "../lib/igAccount";
import { lockedFeatureForPath, usePlan } from "../lib/plan";
import { useExtensionStatus } from "../lib/sso";
import { useExtensionUpdate } from "../pages/extension/api";
import { accountTypeLabel } from "../components/AccountTypeBadge";
import { AssistantWidget } from '../features/assistant/AssistantWidget';
import './mobile-shell.css';
import { AnnouncementBanner } from './AnnouncementBanner';

/** Signed-in app chrome: sidebar, top bar, impersonation banner. Mounts the IG account context. */
export function AppLayout() {
  return (
    <IgAccountProvider>
      <Shell />
      <AssistantWidget />
    </IgAccountProvider>
  );
}

function Shell() {
  const { isImpersonating } = useAuth();
  return (
    <div className={cx("gfp-app", isImpersonating && "gfp-app--impersonating")}>
      <a className="gfp-skip" href="#gfp-main">
        Skip to content
      </a>
      {isImpersonating ? <ImpersonationBanner /> : null}
      <div className="gfp-app__frame">
        <Sidebar />
        <div className="gfp-main">
          <TopBar />
          <AnnouncementBanner />
          <main id="gfp-main" className="gfp-content" tabIndex={-1}>
            {/* A page whose plan feature this account lacks shows the upgrade card instead. */}
            <PlanGate>
              <Outlet />
            </PlanGate>
          </main>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

function NavItem({ to, label, icon: Icon, end, locked }: { to: string; label: string; icon: LucideIcon; end?: boolean; locked?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => cx("gfp-nav__item", isActive && "gfp-nav__item--active")}>
      <Icon size={18} strokeWidth={1.9} aria-hidden="true" className="gfp-nav__icon" />
      <span className="gfp-nav__label">{label}</span>
      {locked ? <PlanLock /> : null}
    </NavLink>
  );
}

/** A collapsible sidebar section: the header toggles, the items slide open beneath it. */
function NavSection({
  id,
  label,
  icon: Icon,
  open,
  active,
  flag,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  icon: LucideIcon;
  open: boolean;
  /** The current page lives in this section. */
  active: boolean;
  /** Something inside needs attention (shown on the header while it's closed). */
  flag?: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = `gfp-nav-${id}`;
  return (
    <div className={cx("gfp-nav__section", open && "gfp-nav__section--open", active && "gfp-nav__section--active")}>
      <button type="button" className="gfp-nav__item gfp-nav__toggle" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        <Icon size={18} strokeWidth={1.9} aria-hidden="true" className="gfp-nav__icon" />
        <span className="gfp-nav__label">{label}</span>
        {flag && !open ? <span className="gfp-nav__updot" aria-label="Needs attention" /> : null}
        <ChevronDown size={16} aria-hidden="true" className="gfp-nav__chev" />
      </button>
      <div id={panelId} className="gfp-nav__panel">
        <div className="gfp-nav__panel-inner">{children}</div>
      </div>
    </div>
  );
}

/** Paths that put you "inside" a section — prefix matches, so detail pages (/crm/:id) count too. */
function inSection(pathname: string, paths: string[]) {
  return paths.some((path) => matchPath({ path, end: false }, pathname));
}

function Sidebar() {
  const { user, canManageCustomers, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const upd = useExtensionUpdate();
  const plan = usePlan();
  const groups = featureNav();
  const accountPaths = ["/help", "/course", "/affiliates", "/settings", "/extension", "/connect-extension", "/reseller", "/admin"];
  const activeSection = groups.find((g) => inSection(pathname, g.items.map((i) => i.to)))?.group ?? (inSection(pathname, accountPaths) ? "account" : null);

  // One section open at a time keeps the whole menu on screen. Moving to a page
  // in another section opens that one; going Home leaves things as they were.
  const [nav, setNav] = useState({ active: activeSection, open: activeSection });
  if (nav.active !== activeSection) setNav({ active: activeSection, open: activeSection ?? nav.open });
  const toggle = (id: string) => setNav((s) => ({ ...s, open: s.open === id ? null : id }));

  const extensionFlag = upd.status === "update_available" || upd.status === "update_required";
  return (
    <aside className="gfp-sidebar" aria-label="Main">
      <Link to="/" className="gfp-sidebar__brand" aria-label="Magically Linked home">
        <Logo size={30} title="" />
      </Link>
      <nav className="gfp-nav">
        <NavItem to="/" end label="Home" icon={House} />
        <NavItem to="/connections" label="Connection requests" icon={UsersRound} />
        {groups.map((g) => (
          <NavSection key={g.group} id={g.group} label={g.label} icon={g.icon} open={nav.open === g.group} active={activeSection === g.group} onToggle={() => toggle(g.group)}>
            {g.items.map((item) => (
              <NavItem key={item.to} to={item.to} label={item.label} icon={item.icon} end={item.end} locked={lockedFeatureForPath(item.to, plan.entitlements) !== null} />
            ))}
          </NavSection>
        ))}
        <div className="gfp-nav__divider" role="separator" />
        <NavSection id="account" label="Account" icon={CircleUser} open={nav.open === "account"} active={activeSection === "account"} flag={extensionFlag} onToggle={() => toggle("account")}>
          <NavItem to="/settings" label="Settings" icon={Settings} />
          <ExtensionNavItem />
          <NavItem to="/help" label="Help center" icon={BookOpen} />
          <NavItem to="/course" label="Course" icon={GraduationCap} />
          <NavItem to="/affiliates" label="Affiliates" icon={Gift} />
          {canManageCustomers ? <NavItem to="/reseller" label="Reseller dashboard" icon={UsersRound} /> : null}
          {isAdmin ? <NavItem to="/admin" label="Admin" icon={ShieldCheck} /> : null}
        </NavSection>
      </nav>
      {user ? (
        <div className="gfp-sidebar__foot">
          <NavLink
            to="/plans"
            className={({ isActive }) => cx("gfp-sidebar__plan", "plans-footlink", isActive && "plans-footlink--active")}
            title="See what each plan includes"
          >
            <span className="plans-footlink__name">{plan.entitlements?.packageName || `${planLabel(user.plan)} plan`}</span>
            <ChevronRight size={12} strokeWidth={2.4} aria-hidden="true" />
          </NavLink>
          {user.role === "reseller" ? (
            <span className="gfp-sidebar__seats">
              {user.seatLimit >= UNLIMITED_SEATS ? `${user.seatsUsed ?? 0} accounts · unlimited seats` : `${user.seatsUsed ?? 0} of ${user.seatLimit} seats`}
            </span>
          ) : null}
        </div>
      ) : null}
      <a className="gfp-sidebar__powered-by" href="https://www.vaughnlabs.io/" target="_blank" rel="noopener noreferrer">
        <span className="gfp-sidebar__powered-dot" aria-hidden="true" />
        <span>Powered by <strong>Vaughn Labs</strong></span>
      </a>
    </aside>
  );
}

// ── Top bar ─────────────────────────────────────────────────────────────────

function TopBar() {
  return (
    <header className="gfp-topbar">
      <IgAccountSwitcher />
      <div className="gfp-topbar__right">
        <ExtensionPill />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

function IgAccountSwitcher() {
  const { accounts, current, switchAccount, isLoading } = useIgAccount();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="gfp-switcher gfp-switcher--loading" aria-hidden="true">
        <Skeleton circle height={32} />
        <Skeleton width={120} height={12} />
      </div>
    );
  }
  if (!current) {
    return (
      <Link to="/connect-extension" className="gfp-switcher gfp-switcher--empty">
        <span className="gfp-switcher__ghost">
          <IgGlyph size={16} />
        </span>
        <span className="gfp-switcher__text">
          <span className="gfp-switcher__name">No LinkedIn account yet</span>
          <span className="gfp-switcher__meta">Connect one with the extension</span>
        </span>
      </Link>
    );
  }

  // Each row: avatar, @handle, followers, type, run status and the browser that runs it.
  const items: MenuItem[] = [
    { type: "label", label: user ? `LinkedIn accounts · ${accounts.length}${user.plan === "lifetime" ? " · Unlimited" : ` of ${user.igAccountLimit}`}` : "LinkedIn accounts" },
    ...accounts.map<MenuItem>((a) => ({
      key: a.id,
      label: <IgSwitcherOption account={a} />,
      icon: <IgStatusAvatar account={a} size={28} />,
      hint: a.id === current.id ? <Check size={15} aria-label="Selected" /> : undefined,
      onSelect: () => switchAccount(a.id),
    })),
    { type: "separator" },
    { type: "label", label: "Selects your workspace; LinkedIn stays signed in as before." },
    { label: "Manage accounts", icon: <Settings size={16} />, onSelect: () => navigate("/settings?tab=instagram") },
    { label: "Add an account", icon: <Plus size={16} />, onSelect: () => navigate(`/settings?tab=instagram#${ADD_ACCOUNT_ANCHOR}`) },
  ];

  return (
    <Menu
      align="start"
      minWidth={300}
      label="Switch LinkedIn account"
      items={items}
      trigger={
        <button type="button" className="gfp-switcher" aria-label={`LinkedIn account: @${current.username}. Switch account`}>
          <IgStatusAvatar account={current} size={32} ring />
          <span className="gfp-switcher__text">
            <span className="gfp-switcher__name">@{current.username}</span>
            <span className="gfp-switcher__meta">
              {formatCount(current.followers)} followers · {accountTypeLabel(current.accountType)}
            </span>
          </span>
          <ChevronsUpDown size={16} className="gfp-switcher__chev" aria-hidden="true" />
        </button>
      }
    />
  );
}

/** One entry for installation, connection and updates. */
function ExtensionNavItem() {
  const upd = useExtensionUpdate();
  const connecting = useMatch("/connect-extension");
  const extension = useMatch("/extension/*");
  const active = Boolean(connecting || extension);
  const flag = upd.status === "update_available" || upd.status === "update_required";
  return (
    <Link to="/extension" aria-current={active ? "page" : undefined} className={cx("gfp-nav__item", active && "gfp-nav__item--active")}>
      <ExtensionGlyph size={18} className="gfp-nav__icon" />
      <span className="gfp-nav__label">Extension</span>
      {flag ? <span className={cx("gfp-nav__updot", upd.status === "update_required" && "gfp-nav__updot--required")} aria-label="Update available" /> : null}
    </Link>
  );
}

function ExtensionPill() {
  const ext = useExtensionStatus();
  const upd = useExtensionUpdate();
  let to = "/connect-extension";
  let modifier: string = ext.status;
  let label =
    ext.status === "connected"
      ? "Extension connected"
      : ext.status === "signed_out"
        ? "Extension signed out"
        : ext.status === "checking"
          ? "Looking for the extension"
          : "Extension not installed";
  if (ext.status === "not_detected") to = "/extension";
  if (upd.status === "update_required") {
    to = "/extension";
    modifier = "required";
    label = "Update required";
  } else if (upd.status === "update_available") {
    to = "/extension";
    modifier = "update";
    label = "Update available";
  }
  const title = ext.version
    ? `Installed v${ext.version}${upd.latest ? ` · latest v${upd.latest.version}` : ""}`
    : upd.latest
      ? `Latest v${upd.latest.version}`
      : undefined;
  return (
    <Link to={to} className={cx("gfp-extpill", `gfp-extpill--${modifier}`)} title={title}>
      <span className="gfp-extpill__dot" aria-hidden="true" />
      {label}
    </Link>
  );
}

function UserMenu() {
  const { user, logout, canManageCustomers, isAdmin } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const items: Array<MenuItem | false> = [
    { label: "Settings", icon: <Settings size={16} />, onSelect: () => navigate("/settings") },
    { label: "Extension", icon: <ExtensionGlyph size={16} />, onSelect: () => navigate("/extension") },
    canManageCustomers && { label: "Reseller dashboard", icon: <Building2 size={16} />, onSelect: () => navigate("/reseller") },
    isAdmin && { label: "Admin", icon: <ShieldCheck size={16} />, onSelect: () => navigate("/admin") },
    { type: "separator" },
    { label: "Log out", icon: <LogOut size={16} />, onSelect: () => void logout() },
  ];
  return (
    <Menu
      align="end"
      minWidth={240}
      label="Account"
      header={
        <div className="gfp-usermenu__head">
          <span className="gfp-usermenu__name">{user.name || user.email}</span>
          <span className="gfp-usermenu__email">{user.email}</span>
          <span className="gfp-usermenu__badges">
            <Badge size="sm">{ROLE_LABELS[user.role]}</Badge>
            <Badge size="sm" tone="accent">
              {planLabel(user.plan)}
            </Badge>
          </span>
        </div>
      }
      items={items}
      trigger={
        <button type="button" className="gfp-userbtn" aria-label={`Account menu for ${user.name || user.email}`}>
          <Avatar name={user.name || user.email} size={30} />
          <span className="gfp-userbtn__name">{user.name ? user.name.split(" ")[0] : <UserRound size={16} />}</span>
        </button>
      }
    />
  );
}

// ── Impersonation ───────────────────────────────────────────────────────────

function ImpersonationBanner() {
  const { user } = useAuth();
  const toast = useToast();
  const stop = useMutation({
    mutationFn: authApi.stopImpersonating,
    onSuccess: (res) => {
      // Full reload: every cache belongs to the customer we were viewing.
      window.location.assign(res.user.role === "admin" ? "/admin" : "/reseller");
    },
    onError: (err) => toast.error(err),
  });
  if (!user) return null;
  return (
    <div className="gfp-impersonation" role="status">
      <span className="gfp-impersonation__text">
        Viewing as <b>{user.name || user.email}</b>
        {user.name ? <span className="gfp-impersonation__email">{user.email}</span> : null}
      </span>
      <Button size="sm" variant="secondary" loading={stop.isPending} onClick={() => stop.mutate()}>
        Return to my account
      </Button>
    </div>
  );
}
